import axiosInstance from "../api.axiosInstance"
import type {
  SolarAuroraLinkPreviewDto,
  SolarAuroraProjectDesignsDto,
  SolarProposalAuroraDto,
} from "../dto/solar-proposal-aurora.dto"

// A link, switch or refresh pulls Aurora, checks equipment with PowurOS and runs
// production inline, so it gets the provider-write timeout rather than
// reporting a failure while the gateway is still saving.
const PROVIDER_WRITE_TIMEOUT_MS = 120_000

const auroraPath = (proposalId: string): string =>
  `/solar-proposal/v2/${encodeURIComponent(proposalId)}/aurora`

/** Solar Proposal V2's Aurora step: stored reads, the first link, and the inline switch and refresh. */
export const solarProposalAuroraService = {
  async getAurora(proposalId: string): Promise<SolarProposalAuroraDto> {
    const response = await axiosInstance.get<SolarProposalAuroraDto>(
      auroraPath(proposalId),
    )
    return response.data
  },

  /** One Aurora call at the gateway; nothing is written. */
  async getProjectDesigns(
    proposalId: string,
  ): Promise<SolarAuroraProjectDesignsDto> {
    const response = await axiosInstance.get<SolarAuroraProjectDesignsDto>(
      `${auroraPath(proposalId)}/project-designs`,
    )
    return response.data
  },

  /** One Aurora design read and one project read at the gateway; nothing is written. */
  async getLinkPreview(
    proposalId: string,
    ref: string,
  ): Promise<SolarAuroraLinkPreviewDto> {
    const response = await axiosInstance.get<SolarAuroraLinkPreviewDto>(
      `${auroraPath(proposalId)}/link-preview`,
      { params: { ref } },
    )
    return response.data
  },

  /** An administrator links the first Aurora design: the Source Lock. */
  async postLink(
    proposalId: string,
    ref: string,
  ): Promise<SolarProposalAuroraDto> {
    const response = await axiosInstance.post<SolarProposalAuroraDto>(
      `${auroraPath(proposalId)}/link`,
      { ref },
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )
    return response.data
  },

  async postVersion(
    proposalId: string,
    auroraDesignId: string,
  ): Promise<SolarProposalAuroraDto> {
    const response = await axiosInstance.post<SolarProposalAuroraDto>(
      `${auroraPath(proposalId)}/versions`,
      { auroraDesignId },
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )
    return response.data
  },

  /** Bodyless: the gateway re-pulls the active Aurora design. */
  async postRefresh(proposalId: string): Promise<SolarProposalAuroraDto> {
    const response = await axiosInstance.post<SolarProposalAuroraDto>(
      `${auroraPath(proposalId)}/refresh`,
      undefined,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )
    return response.data
  },
}
